import { useState } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import InputBase from '@mui/material/InputBase';
import {
  useOptionsQuery, useTestRegistryQuery,
  useAddOptionMutation, useRemoveOptionMutation, useResetOptionMutation,
} from '../../services/api';
import DataCard from '../../components/common/DataCard';
import AppButton from '../../components/common/AppButton';
import QueryState from '../../components/common/QueryState';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { useToast } from '../../components/common/Toast';
import { ReadOnlyNotice } from '../../components/common/Access';
import { usePermissions } from '../../features/auth/permissions';
import { Shimmer, ShimmerCard } from '../../components/common/Shimmer';
import { SectionHeading } from '../../components/common/Bits';
import { CONTROL_SX } from '../../components/common/FormField';

const OPT_META = [
  { key: 'titles', label: 'Name titles', desc: 'Salutations in the D2C patient lead form', where: 'Leads · D2C' },
  { key: 'genders', label: 'Gender', desc: 'Patient gender options', where: 'Leads · D2C' },
  { key: 'sources', label: 'Lead sources', desc: 'How a lead was generated', where: 'Leads · all channels' },
  { key: 'stages', label: 'Pipeline stages', desc: 'Columns of the leads pipeline (Won & Lost are terminal)', where: 'Leads pipeline' },
  { key: 'roles', label: 'User roles', desc: 'Access level for team members', where: 'Settings · Users' },
];

function OptChip({ children, tone, onRemove, meta, busy }) {
  const bg = tone === 'pop' ? '#eaf2fd' : tone === 'auto' ? '#eef8f2' : 'var(--card-2)';
  const bc = tone === 'pop' ? '#cfe0f7' : tone === 'auto' ? '#cdead9' : 'var(--border)';
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: '6px',
        p: '5px 11px', borderRadius: '999px', background: bg,
        border: `1px solid ${bc}`, fontSize: 12.5,
        opacity: busy ? 0.5 : 1,
      }}
    >
      {children}
      {meta && (
        <Box component="span" sx={{ fontFamily: 'var(--mono)', fontSize: 9.5, color: 'var(--tealdim)', textTransform: 'uppercase' }}>
          {meta}
        </Box>
      )}
      {onRemove && (
        <Box
          component="button"
          disabled={busy}
          onClick={onRemove}
          sx={{
            border: 0, background: 'transparent', cursor: busy ? 'default' : 'pointer', color: 'var(--muted)',
            fontSize: 11, lineHeight: 1, p: '0 0 0 2px', '&:hover': { color: 'var(--crit)' },
          }}
        >
          ✕
        </Box>
      )}
    </Box>
  );
}

function OptionCard({ meta, values, busy, canEdit, onAdd, onRemove, onReset }) {
  const [draft, setDraft] = useState('');

  const commit = async () => {
    const v = draft.trim();
    if (!v) return;
    // clear straight away so a fast typist can queue the next one
    setDraft('');
    const ok = await onAdd(meta.key, v);
    if (!ok) setDraft(v);
  };

  return (
    <DataCard
      stretch
      title={meta.label}
      subtitle={
        <>
          {meta.desc} · <Box component="span" sx={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--tealdim)' }}>{meta.where}</Box>
        </>
      }
      action={canEdit ? <AppButton mini disabled={busy} onClick={() => onReset(meta)}>Reset</AppButton> : null}
      sx={{ display: 'flex', flexDirection: 'column' }}
    >
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
        {values.map((v) => (
          <OptChip key={v} busy={busy} onRemove={canEdit ? () => onRemove(meta, v) : undefined}>{v}</OptChip>
        ))}
      </Box>
      {canEdit && (
      <Grid container spacing={1}>
        <Grid size={{ xs: 12, sm: 8 }}>
          <InputBase
            fullWidth
            sx={CONTROL_SX}
            placeholder={`Add ${meta.label.toLowerCase()}…`}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(); } }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <AppButton fullWidth disabled={busy} onClick={commit}>＋ Add</AppButton>
        </Grid>
      </Grid>
      )}
    </DataCard>
  );
}

/**
 * The test registry is three lists merged server-side: popular predefined tests tied
 * to the tracked conditions, whatever the ledger has actually seen, and custom
 * additions. Only the custom ones can be removed — the other two are facts, not settings.
 */
function TestsCard({ busy, canEdit, onAdd, onRemove }) {
  const [draft, setDraft] = useState('');
  const query = useTestRegistryQuery();

  const Group = ({ label, children }) => (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ fontFamily: 'var(--mono)', fontSize: 10.5, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--tealdim)', mb: 1 }}>
        {label}
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>{children}</Box>
    </Box>
  );

  const commit = async () => {
    const v = draft.trim();
    if (!v) return;
    setDraft('');
    const ok = await onAdd('tests', v);
    if (!ok) setDraft(v);
  };

  return (
    <DataCard>
      <QueryState
        query={query}
        skeleton={
          <Box>
            <Shimmer width="70%" height={12} />
            <Shimmer width="55%" height={12} sx={{ mt: 1 }} />
            {[0, 1, 2].map((g) => (
              <Box key={g} sx={{ mt: 2.5 }}>
                <Shimmer width={220} height={10} />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.25 }}>
                  {Array.from({ length: g === 0 ? 10 : 5 }, (_, i) => (
                    <Shimmer key={i} width={130 + ((i * 37) % 90)} height={26} radius={999} />
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        }
      >
        {(reg) => (
          <>
            <Box sx={{ fontSize: 12, color: 'var(--muted)', maxWidth: 840 }}>
              The tests list is <b>auto-populated from tests actually performed</b> for patients/customers (pulled from
              transactions), plus <b>popular predefined tests</b> that map to the 10 tracked conditions. Add custom tests
              below — everything merges into one registry of <b>{reg.total} tests</b>.
            </Box>

            <Group label="★ Popular predefined · linked to conditions">
              {reg.popular.map((t) => (
                <OptChip key={t.name} tone="pop" meta={t.conditions.join(' · ')}>{t.name}</OptChip>
              ))}
            </Group>

            <Group label={`⟳ Auto-populated from transactions · ${reg.auto.length} tests`}>
              {reg.auto.length ? (
                reg.auto.map((t) => (
                  <OptChip key={t.name} tone="auto" meta={`${t.count}×`}>{t.name}</OptChip>
                ))
              ) : (
                <Box sx={{ fontSize: 12, color: 'var(--muted)' }}>
                  None yet — tests appear here automatically as they are performed.
                </Box>
              )}
            </Group>

            <Group label="＋ Custom tests">
              {reg.custom.length ? (
                reg.custom.map((t) => (
                  <OptChip key={t.name} busy={busy} onRemove={canEdit ? () => onRemove({ key: 'tests', label: 'test' }, t.name) : undefined}>
                    {t.name}
                  </OptChip>
                ))
              ) : (
                <Box sx={{ fontSize: 12, color: 'var(--muted)' }}>No custom tests added.</Box>
              )}
            </Group>
          </>
        )}
      </QueryState>

      {canEdit && (
      <Grid container spacing={1} sx={{ mt: 1.25 }}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <InputBase
            fullWidth
            sx={CONTROL_SX}
            placeholder="Add a custom test…"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(); } }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 'auto' }}>
          <AppButton disabled={busy} onClick={commit}>＋ Add test</AppButton>
        </Grid>
      </Grid>
      )}
    </DataCard>
  );
}

function OptionsSkeleton() {
  return (
    <Grid container spacing={2}>
      {OPT_META.map((m) => (
        <Grid key={m.key} size={{ xs: 12, md: 6, xl: 4 }}>
          <ShimmerCard>
            <Shimmer width="40%" height={14} />
            <Shimmer width="70%" height={10} sx={{ mt: 1 }} />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2, mb: 2 }}>
              {Array.from({ length: 5 }, (_, i) => (
                <Shimmer key={i} width={70 + ((i * 29) % 60)} height={26} radius={999} />
              ))}
            </Box>
            <Shimmer height={38} radius={12} />
          </ShimmerCard>
        </Grid>
      ))}
    </Grid>
  );
}

export default function OptionsPage() {
  const toast = useToast();
  const { canEdit, role } = usePermissions();
  const query = useOptionsQuery();
  const [addOption, addState] = useAddOptionMutation();
  const [removeOption, removeState] = useRemoveOptionMutation();
  const [resetOption, resetState] = useResetOptionMutation();
  const [confirm, setConfirm] = useState(null);

  // a read-only account gets the same treatment as a write in flight: nothing to click
  const busy = !canEdit || addState.isLoading || removeState.isLoading || resetState.isLoading;
  const fail = (e, fallback) => toast(e?.data?.error || e?.message || fallback, 'error');

  /** Returns true when the value was accepted, so the field can stay cleared. */
  const add = async (key, value) => {
    try {
      await addOption({ key, value }).unwrap();
      toast(`Added “${value}”`);
      return true;
    } catch (e) {
      fail(e, "Couldn't add that option");
      return false;
    }
  };

  const askRemove = (meta, value) =>
    setConfirm({
      title: `Remove ${meta.label.toLowerCase()}`,
      message: `Remove “${value}” from ${meta.label.toLowerCase()}?`,
      detail:
        meta.key === 'stages'
          ? 'Leads already sitting in this stage keep it — the pipeline reports them separately.'
          : 'Forms reading this list stop offering it. Records already using it are unchanged.',
      confirmLabel: 'Remove',
      tone: 'danger',
      run: async () => {
        const res = await removeOption({ key: meta.key, value }).unwrap();
        // the server counts what a removed stage left behind rather than rewriting leads
        if (res?.orphanedLeads) {
          toast(`Removed “${value}” — ${res.orphanedLeads} lead(s) are still in that stage`, 'info');
        } else {
          toast(`Removed “${value}”`);
        }
      },
      fallback: "Couldn't remove that option",
    });

  const askReset = (meta) =>
    setConfirm({
      title: `Reset ${meta.label.toLowerCase()}`,
      message: `Reset ${meta.label.toLowerCase()} to the defaults?`,
      detail: 'Anything added to this list is discarded.',
      confirmLabel: 'Reset',
      run: async () => {
        await resetOption(meta.key).unwrap();
        toast(`${meta.label} reset to defaults`);
      },
      fallback: "Couldn't reset that list",
    });

  const runConfirm = async () => {
    const c = confirm;
    setConfirm(null);
    try {
      await c.run();
    } catch (e) {
      fail(e, c.fallback);
    }
  };

  return (
    <>
      <Box sx={{ color: 'var(--ink-2)', maxWidth: 780, m: '0 0 18px' }}>
        Every pick-list in the platform reads its choices from here.
        {canEdit && ' Add, remove, or reset options and the change flows straight into the relevant forms.'}
      </Box>

      {!canEdit && <ReadOnlyNotice role={role} what="these pick-lists" />}

      <QueryState query={query} skeleton={<OptionsSkeleton />}>
        {(options) => (
          <Grid container spacing={2}>
            {OPT_META.map((m) => (
              <Grid key={m.key} size={{ xs: 12, md: 6, xl: 4 }}>
                <OptionCard
                  meta={m}
                  values={options[m.key] || []}
                  busy={busy}
                  canEdit={canEdit}
                  onAdd={add}
                  onRemove={askRemove}
                  onReset={askReset}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </QueryState>

      <SectionHeading>Tests — auto-populated registry</SectionHeading>
      <TestsCard busy={busy} canEdit={canEdit} onAdd={add} onRemove={askRemove} />

      <ConfirmDialog
        open={!!confirm}
        tone={confirm?.tone}
        title={confirm?.title}
        message={confirm?.message}
        detail={confirm?.detail}
        confirmLabel={confirm?.confirmLabel}
        onCancel={() => setConfirm(null)}
        onConfirm={runConfirm}
      />
    </>
  );
}
